import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import { apiErrorMessage, toDisplayError } from '../utils/apiError';
import { useSingleUseToken } from '../utils/singleUseToken';

// Validation schema
const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'auth.validation.passwordTooShort')
      .regex(/[A-Z]/, 'auth.validation.passwordUppercase')
      .regex(/[a-z]/, 'auth.validation.passwordLowercase')
      .regex(/[0-9]/, 'auth.validation.passwordNumber'),
    confirmPassword: z.string().min(1, 'auth.validation.confirmPasswordRequired'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'auth.validation.passwordsMustMatch',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // REV-878: the email links to /reset-password#token=…; the hook reads the
  // fragment and strips it from the URL. The token then lives only here, and
  // goes to the API in the request body.
  const token = useSingleUseToken();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // No token provided
  if (!token) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
          {t('auth.resetPassword.invalidLink')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('auth.resetPassword.invalidLinkMessage')}
        </Typography>
        <Button component={RouterLink} to="/forgot-password" variant="contained">
          {t('auth.resetPassword.requestNewLink')}
        </Button>
      </Box>
    );
  }

  const onSubmit = async (data: ResetPasswordFormData) => {
    setServerError(null);
    setIsSubmitting(true);

    try {
      await apiClient.post('/v1/auth/reset-password', {
        token,
        newPassword: data.password,
      });

      setIsSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        void navigate('/login', {
          state: { message: t('auth.resetPassword.successMessage') },
        });
      }, 3000);
    } catch (error: unknown) {
      // `error` is a string on some endpoints and { message, code } on others;
      // calling .includes() straight on it threw for the object form (REV-509).
      const apiMsg = apiErrorMessage(error);
      if (apiMsg?.includes('expired')) {
        setServerError(t('auth.errors.tokenExpired'));
      } else if (apiMsg?.includes('invalid')) {
        setServerError(t('auth.errors.tokenInvalid'));
      } else {
        setServerError(toDisplayError(error, t('auth.errors.resetFailed')));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
          {t('auth.resetPassword.successTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.resetPassword.successMessage')}
        </Typography>
        <CircularProgress size={24} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {t('auth.resetPassword.redirecting')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
          {t('auth.resetPassword.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('auth.resetPassword.subtitle')}
        </Typography>
      </Box>

      {/* Server error alert */}
      {serverError && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {serverError}
        </Alert>
      )}

      {/* Reset password form */}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('password')}
          label={t('auth.fields.newPassword')}
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          error={!!errors.password}
          helperText={errors.password ? t(errors.password.message as string) : undefined}
          disabled={isSubmitting}
          sx={{ mb: 2 }}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <TextField
          {...register('confirmPassword')}
          label={t('auth.fields.confirmPassword')}
          type={showConfirmPassword ? 'text' : 'password'}
          autoComplete="new-password"
          error={!!errors.confirmPassword}
          helperText={
            errors.confirmPassword ? t(errors.confirmPassword.message as string) : undefined
          }
          disabled={isSubmitting}
          sx={{ mb: 1 }}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')
                    }
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Password requirements hint */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
          {t('auth.register.passwordHint')}
        </Typography>

        {/* Submit button */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('auth.resetPassword.submit')
          )}
        </Button>
      </Box>

      {/* Back to login link */}
      <Box sx={{ textAlign: 'center' }}>
        <Link
          component={RouterLink}
          to="/login"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <ArrowBackIcon fontSize="small" />
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </Box>
    </Box>
  );
}

export default ResetPasswordPage;
